import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import { useStock } from "../../StockContext";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  annotationPlugin
);

function getDateLimit(period) {
  const now = new Date();
  let dateLimit;
  switch (period) {
    case "1U":
      dateLimit = new Date(now.setDate(now.getDate() - 7));
      break;
    case "1M":
      dateLimit = new Date(now.setMonth(now.getMonth() - 1));
      break;
    case "3M":
      dateLimit = new Date(now.setMonth(now.getMonth() - 3));
      break;
    case "ÅTD":
      dateLimit = new Date(now.getFullYear(), 0, 1);
      break;
    case "1 år":
      dateLimit = new Date(now.setFullYear(now.getFullYear() - 1));
      break;
    default:
      dateLimit = null;
  }
  return dateLimit;
}

function DashboardCard01() {
  const { stockSelected1 } = useStock();
  const [timePeriod, setTimePeriod] = useState("1M");
  const [chartData, setChartData] = useState(null);
  const [closingPrice, setClosingPrice] = useState("N/A");
  const [percentageChange, setPercentageChange] = useState("N/A");
  const [chartColor, setChartColor] = useState("rgba(128, 128, 128, 1)");
  const [stockData, setStockData] = useState(null);
  const [currency, setCurrency] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getStockDisplayName = () => {
    if (isLoading) return "Loading...";
    if (error) return "Error loading stock";
    if (!stockData) return "Select a stock";
    return stockData.shortName || stockData.longName || stockData.symbol || "Unknown Stock";
  };

  useEffect(() => {
    if (!stockSelected1 || !stockSelected1.symbol) return;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [stockResponse, recentResponse, historicalResponse, financialsResponse] = await Promise.all([
          fetch(`https://investorverden.onrender.com/api/stocks?q=${stockSelected1.symbol}`),
          fetch(`https://investorverden.onrender.com/api/stock-price?q=${stockSelected1.symbol}`),
          fetch(`https://investorverden.onrender.com/api/historical?q=${stockSelected1.symbol}&range=1y&interval=1d`),
          fetch(`https://investorverden.onrender.com/api/financials?q=${stockSelected1.symbol}`)
        ]);

        if (!stockResponse.ok || !recentResponse.ok || !historicalResponse.ok || !financialsResponse.ok) {
          throw new Error('One or more API calls failed');
        }

        const [stockData, recentData, historicalData, financialsData] = await Promise.all([
          stockResponse.json(),
          recentResponse.json(),
          historicalResponse.json(),
          financialsResponse.json()
        ]);

        if (stockData.error) throw new Error(stockData.error);
        
        setStockData(stockData.stockData);
        if (!recentData.error) {
          setClosingPrice(recentData.currentPrice);
        }

        const historicalPrices = historicalData.stockData;
        if (!historicalPrices || historicalPrices.length === 0) {
          throw new Error('No historical data available');
        }

        setCurrency(financialsData.summaryDetail?.currency || 'USD');

        const dateLimit = getDateLimit(timePeriod);
        const filteredData = historicalPrices.filter(
          (item) => !dateLimit || new Date(item.date) >= dateLimit
        );

        const labels = filteredData.map((item) =>
          new Date(item.date).toISOString().split("T")[0]
        );
        const prices = filteredData.map((item) => item.close);

        const initialPrice = prices[0];
        const finalPrice = prices[prices.length - 1];
        const change = ((finalPrice - initialPrice) / initialPrice) * 100;
        setPercentageChange(change.toFixed(2));

        setChartData({
          labels: labels,
          datasets: [
            {
              data: prices,
              fill: true,
              backgroundColor: change > 0 ? "rgba(0, 128, 0, 0.2)" : "rgba(255, 0, 0, 0.2)",
              borderColor: change > 0 ? "rgba(0, 128, 0, 1)" : "rgba(255, 0, 0, 1)",
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 0,
              tension: 0.4,
            },
          ],
        });

        setChartColor(change > 0 ? "rgba(0, 128, 0, 1)" : "rgba(255, 0, 0, 1)");

      } catch (error) {
        console.error("Error fetching stock data:", error);
        setError(error.message || "Could not fetch stock data");
        setChartData(null);
        setClosingPrice("N/A");
        setPercentageChange("N/A");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [stockSelected1, timePeriod]);

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  return (
    <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-12 bg-white dark:bg-gray-800 shadow-sm rounded-xl">
      <div className="px-5 pt-5">
        <header className="flex justify-between items-start mb-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
            {getStockDisplayName()}
          </h2>
        </header>
        <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">
          {closingPrice} {currency}
          <span className={`text-sm ml-4 ${percentageChange > 0 ? "text-green-500" : "text-red-500"}`}>
            ({percentageChange}%)
          </span>
        </div>
      </div>

      <div className="px-5 py-2">
        <div className="flex space-x-2">
          {["1U", "1M", "3M", "ÅTD", "1 år"].map((period) => (
            <button
              key={period}
              className={`btn px-2 py-1 text-xs border ${
                timePeriod === period
                  ? "bg-blue-500 text-white"
                  : "bg-transparent border-blue-500 text-blue-500"
              } hover:bg-blue-200`}
              onClick={() => setTimePeriod(period)}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-5 flex-grow">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : chartData ? (
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                x: {
                  grid: { display: false },
                },
                y: {
                  beginAtZero: false,
                  grid: { display: false },
                },
              },
              plugins: {
                legend: { display: false },
                tooltip: {
                  mode: 'index',
                  intersect: false,
                  callbacks: {
                    label: (context) => {
                      const value = context.parsed.y;
                      return `${currency}${value.toLocaleString()}`;
                    },
                  },
                },
                annotation: {
                  annotations: {
                    verticalLine: {
                      type: 'line',
                      yMin: 'bottom',
                      yMax: 'top',
                      borderColor: 'rgba(194, 194, 194, 0.5)',
                      borderWidth: 2,
                      borderDash: [2, 2],
                      display: false,
                    }
                  }
                }
              },
              onHover: (event, elements, chart) => {
                if (!event || !chart) return;
                
                const verticalLine = chart.options.plugins.annotation.annotations.verticalLine;
                
                if (elements.length > 0) {
                  const xScale = chart.scales.x;
                  verticalLine.display = true;
                  verticalLine.xMin = verticalLine.xMax = xScale.getValueForPixel(event.x);
                  chart.update('none');
                } else {
                  verticalLine.display = false;
                  chart.update('none');
                }
              },
              interaction: {
                mode: 'index',
                intersect: false,
              },
            }}
            height={300}
          />
        ) : (
          <div className="text-center py-12">No data available</div>
        )}
      </div>
    </div>
  );
}

export default DashboardCard01;